import React, { useEffect, useState } from 'react'
import { T, fmtOdds } from './ui'

// Odds-range filter — a global "only show props priced between X and Y" control.
// One shared range (persisted per device) applies to every priced play board
// (Players → Rankings, Touchdowns → Pay Dirt / Value TDs) so a user who only
// plays −110 to +200 sets it once. Rows with no book price are hidden while a
// range is on (we can't place them in it). min/max are American odds; either
// end may be null (open-ended).

const KEY = 'brkr_nfl_odds_range'
const _subs = new Set()
let _range = (() => {
  try { const r = JSON.parse(localStorage.getItem(KEY) || 'null'); return r && (r.min != null || r.max != null) ? r : null }
  catch (_) { return null }
})()

export const getOddsRange = () => _range
export function setOddsRange(r) {
  _range = r && (r.min != null || r.max != null) ? { min: r.min ?? null, max: r.max ?? null } : null
  try { _range ? localStorage.setItem(KEY, JSON.stringify(_range)) : localStorage.removeItem(KEY) } catch (_) {}
  _subs.forEach(f => f(_range))
}
export function useOddsRange() {
  const [r, setR] = useState(_range)
  useEffect(() => { _subs.add(setR); return () => { _subs.delete(setR) } }, [])
  return r
}

// American → decimal, so "−110 to +200" compares on one monotonic payout scale.
const dec = o => (o > 0 ? 1 + o / 100 : 1 + 100 / -o)
export const validOdds = o => Number.isFinite(o) && (o >= 100 || o <= -100)
export function inOddsRange(range, odds) {
  if (!range) return true
  const o = Number(odds)
  if (odds == null || odds === '' || !validOdds(o)) return false
  const d = dec(o)
  if (range.min != null && d < dec(range.min) - 1e-9) return false
  if (range.max != null && d > dec(range.max) + 1e-9) return false
  return true
}
export function oddsRangeLabel(r) {
  if (!r) return 'Any price'
  if (r.min != null && r.max != null) return `${fmtOdds(r.min)} to ${fmtOdds(r.max)}`
  if (r.min != null) return `${fmtOdds(r.min)} or longer`
  return `${fmtOdds(r.max)} or shorter`
}

const PRESETS = [
  ['Any price', null],
  ['Favorites', { min: -300, max: -110 }],
  ['Sweet spot', { min: -110, max: 200 }],
  ['Near even', { min: -150, max: 150 }],
  ['Plus money', { min: 100, max: 400 }],
  ['Long shots', { min: 250, max: null }],
]
const same = (a, b) => (!a && !b) || (a && b && a.min === b.min && a.max === b.max)

// Parse a typed American price: accepts "+200", "200", "-110", "−110". Blank → null.
function parse(s) {
  const t = String(s || '').trim().replace(/[−–]/g, '-').replace(/^\+/, '')
  if (!t) return { v: null }
  const n = Number(t)
  if (!validOdds(n)) return { err: 'Use American odds like −110 or +200' }
  return { v: Math.round(n) }
}

export function OddsRangeButton({ style }) {
  const r = useOddsRange()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Filter by odds range" style={{
        fontSize: 10.5, fontWeight: 800, cursor: 'pointer', borderRadius: 999, padding: '5px 10px', whiteSpace: 'nowrap',
        border: `1px solid ${r ? T.green : T.border2}`, background: r ? T.greenDim : T.card2, color: r ? T.green : T.muted, ...style }}>
        💲 {r ? oddsRangeLabel(r) : 'Odds'}
      </button>
      {open && <OddsDrawer onClose={() => setOpen(false)} />}
    </>
  )
}

// Thin bar under the header while a range is on — the same "a filter is narrowing
// this board" feedback the game filter gives, with one-tap clear.
export function OddsRangeBar() {
  const r = useOddsRange()
  const [open, setOpen] = useState(false)
  if (!r) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 16px', background: T.greenDim, borderBottom: `1px solid ${T.border}` }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>💲 Priced <b style={{ color: T.green }}>{oddsRangeLabel(r)}</b> only</span>
      <button onClick={() => setOpen(true)} style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 800, color: T.green, background: 'transparent',
        border: `1px solid ${T.green}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>Edit</button>
      <button onClick={() => setOddsRange(null)} style={{ fontSize: 11.5, fontWeight: 800, color: T.muted, background: 'transparent',
        border: `1px solid ${T.border}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>✕ Clear</button>
      {open && <OddsDrawer onClose={() => setOpen(false)} />}
    </div>
  )
}

export function OddsDrawer({ onClose }) {
  const cur = getOddsRange()
  const [minS, setMinS] = useState(cur?.min != null ? fmtOdds(cur.min) : '')
  const [maxS, setMaxS] = useState(cur?.max != null ? fmtOdds(cur.max) : '')
  const pMin = parse(minS), pMax = parse(maxS)
  let err = pMin.err || pMax.err
  if (!err && pMin.v != null && pMax.v != null && dec(pMin.v) > dec(pMax.v)) err = 'The shortest price goes on the left (e.g. −110 to +200)'
  const draft = err ? null : (pMin.v == null && pMax.v == null ? null : { min: pMin.v, max: pMax.v })
  const apply = r => { setOddsRange(r); onClose() }
  const pick = r => { setMinS(r?.min != null ? fmtOdds(r.min) : ''); setMaxS(r?.max != null ? fmtOdds(r.max) : '') }
  useEffect(() => {
    const k = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k)
  }, [onClose])
  const inp = { flex: 1, minWidth: 0, background: T.bg, border: `1px solid ${T.border2}`, borderRadius: 10, color: T.text,
    fontSize: 18, fontWeight: 800, fontFamily: T.mono, padding: '11px 12px', textAlign: 'center', boxSizing: 'border-box', width: '100%' }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000A', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div role="dialog" aria-label="Odds range" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, boxSizing: 'border-box', background: T.card,
        borderTop: `1px solid ${T.border2}`, borderRadius: '18px 18px 0 0', padding: '14px 18px calc(18px + env(safe-area-inset-bottom))' }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: T.border2, margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: T.text }}>Odds range</div>
          <button onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.muted, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginTop: 4, lineHeight: 1.45 }}>
          Only show props whose book price is in this range. Applies to Players and Touchdowns boards. Plays without a price are hidden while it's on.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {PRESETS.map(([lbl, r]) => {
            const on = same(draft, r)
            return <button key={lbl} onClick={() => pick(r)} style={{ fontSize: 12, fontWeight: 800, borderRadius: 999, padding: '7px 12px', cursor: 'pointer',
              border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card2, color: on ? T.green : T.muted2 }}>
              {lbl}{r ? <span style={{ fontWeight: 600, color: T.muted, marginLeft: 5 }}>{oddsRangeLabel(r)}</span> : null}</button>
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '.06em' }}>
            SHORTEST<input inputMode="text" placeholder="−110" value={minS} onChange={e => setMinS(e.target.value)} style={inp} /></label>
          <span style={{ color: T.muted, fontWeight: 800, paddingTop: 18 }}>to</span>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '.06em' }}>
            LONGEST<input inputMode="text" placeholder="+200" value={maxS} onChange={e => setMaxS(e.target.value)} style={inp} /></label>
        </div>
        <div style={{ fontSize: 11.5, minHeight: 18, marginTop: 6, color: err ? T.amber : T.muted }}>
          {err || (draft ? `Showing props priced ${oddsRangeLabel(draft)}.` : 'Leave a side blank for no limit.')}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button onClick={() => apply(null)} style={{ flex: 1, padding: '12px 0', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 800,
            background: 'transparent', color: T.muted, border: `1px solid ${T.border2}` }}>Clear</button>
          <button disabled={!!err} onClick={() => apply(draft)} style={{ flex: 2, padding: '12px 0', borderRadius: 12, cursor: err ? 'default' : 'pointer', fontSize: 14, fontWeight: 900,
            background: err ? T.card2 : T.green, color: err ? T.muted : '#04140D', border: 'none' }}>Apply</button>
        </div>
      </div>
    </div>
  )
}
